import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { endpoints } from "../lib/api";

const MODE_LABELS = {
  both: "Ensemble (lama)",
  claude: "Claude AI",
  neurobro: "Neurobro AI",
  unknown: "Sebelum fitur (lama)",
};

const BUCKET_COLORS = {
  "0-49": "text-signal-sell",
  "50-69": "text-signal-warn",
  "70-100": "text-signal-buy",
};

const fmt = (n, d = 4) =>
  n === undefined || n === null
    ? "-"
    : Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function History() {
  const [trades, setTrades] = useState([]);
  const [aiStats, setAiStats] = useState([]);
  const [confStats, setConfStats] = useState([]);

  useEffect(() => {
    const load = () => {
      endpoints.history(200).then((r) => setTrades(r.data.trades || []));
      endpoints.aiModeStats().then((r) => setAiStats(r.data.modes || [])).catch(() => {});
      endpoints.confidenceStats().then((r) => setConfStats(r.data.sources || [])).catch(() => {});
    };
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  const totalPnl = trades.reduce((s, t) => s + (t.pnl || 0), 0);
  const wins = trades.filter((t) => t.pnl > 0).length;

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar />
      <main className="flex-1 p-6 space-y-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Journal
          </div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Trade History</h1>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Stat label="Total Trades" value={trades.length} />
          <Stat
            label="Cumulative PnL"
            value={`${totalPnl >= 0 ? "+" : ""}$${fmt(totalPnl, 2)}`}
            color={totalPnl >= 0 ? "text-signal-buy" : "text-signal-sell"}
          />
          <Stat
            label="Win Rate"
            value={trades.length ? `${((wins / trades.length) * 100).toFixed(1)}%` : "0%"}
          />
        </div>

        {aiStats.length > 0 && (
          <div className="border border-border rounded-sm bg-[#0d0d0d] p-4 space-y-3" data-testid="ai-mode-stats">
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Performa per AI Mode
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-[10px] font-mono uppercase text-muted-foreground border-b border-border">
                    <th className="text-left px-3 py-2">AI Mode</th>
                    <th className="text-right px-3 py-2">Trades</th>
                    <th className="text-right px-3 py-2">Win</th>
                    <th className="text-right px-3 py-2">Loss</th>
                    <th className="text-right px-3 py-2">Win Rate</th>
                    <th className="text-right px-3 py-2">Total PnL</th>
                    <th className="text-right px-3 py-2">Avg PnL</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {aiStats.map((m) => (
                    <tr key={m.mode} data-testid={`ai-mode-stat-${m.mode}`} className="border-b border-border/50 last:border-0">
                      <td className="px-3 py-2 text-white uppercase">{MODE_LABELS[m.mode] || m.mode}</td>
                      <td className="px-3 py-2 text-right">{m.trades}</td>
                      <td className="px-3 py-2 text-right text-signal-buy">{m.wins}</td>
                      <td className="px-3 py-2 text-right text-signal-sell">{m.losses}</td>
                      <td className="px-3 py-2 text-right">{m.win_rate}%</td>
                      <td className={`px-3 py-2 text-right ${m.total_pnl >= 0 ? "text-signal-buy" : "text-signal-sell"}`}>
                        {m.total_pnl >= 0 ? "+" : ""}${fmt(m.total_pnl, 2)}
                      </td>
                      <td className={`px-3 py-2 text-right ${m.avg_pnl >= 0 ? "text-signal-buy" : "text-signal-sell"}`}>
                        {m.avg_pnl >= 0 ? "+" : ""}${fmt(m.avg_pnl, 2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {confStats.length > 0 && (
          <div className="border border-border rounded-sm bg-[#0d0d0d] p-4 space-y-3" data-testid="confidence-stats">
            <div className="flex items-center justify-between">
              <h3 className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Win Rate per Rentang Confidence
              </h3>
              <span className="font-mono text-[10px] text-muted-foreground">
                akurasi sinyal per tingkat keyakinan AI
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {confStats.map((src) => (
                <div
                  key={src.mode}
                  data-testid={`confidence-stats-${src.mode}`}
                  className="border border-border rounded-sm bg-[#0a0a0a] p-3 space-y-2"
                >
                  <div className={`font-heading font-black text-sm ${src.mode === "claude" ? "text-signal-brand" : "text-signal-buy"}`}>
                    {MODE_LABELS[src.mode] || src.mode.toUpperCase()}
                  </div>
                  {src.buckets.map((b) => (
                    <div key={b.range} data-testid={`conf-bucket-${src.mode}-${b.range}`} className="space-y-1">
                      <div className="flex items-center justify-between font-mono text-[11px]">
                        <span className={`font-bold ${BUCKET_COLORS[b.range]}`}>{b.range}%</span>
                        <span className="text-muted-foreground">
                          {b.trades} trade{b.trades !== 1 ? "s" : ""} ·{" "}
                          <span className="text-white">WR {b.win_rate}%</span> ·{" "}
                          <span className={b.total_pnl >= 0 ? "text-signal-buy" : "text-signal-sell"}>
                            {b.total_pnl >= 0 ? "+" : ""}${fmt(b.total_pnl, 2)}
                          </span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-border rounded-sm overflow-hidden">
                        <div
                          className={`h-full ${b.win_rate >= 50 ? "bg-signal-buy" : "bg-signal-sell"}`}
                          style={{ width: `${Math.min(100, b.win_rate)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border border-border rounded-sm bg-[#0d0d0d]" data-testid="history-table">
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[10px] font-mono uppercase text-muted-foreground border-b border-border">
                  <th className="text-left px-4 py-2">Closed</th>
                  <th className="text-left px-4 py-2">Symbol</th>
                  <th className="text-left px-4 py-2">Side</th>
                  <th className="text-right px-4 py-2">Conf</th>
                  <th className="text-right px-4 py-2">Entry</th>
                  <th className="text-right px-4 py-2">Exit</th>
                  <th className="text-right px-4 py-2">Lev</th>
                  <th className="text-right px-4 py-2">PnL</th>
                  <th className="text-left px-4 py-2">Reason</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {trades.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-8 text-muted-foreground">
                      No closed trades yet
                    </td>
                  </tr>
                )}
                {trades.map((t) => {
                  const pnlPos = (t.pnl || 0) >= 0;
                  return (
                    <tr key={t.id} className="border-b border-border">
                      <td className="px-4 py-3 text-muted-foreground">
                        {t.closed_at ? new Date(t.closed_at).toLocaleString() : "-"}
                      </td>
                      <td className="px-4 py-3 font-bold">{t.symbol}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-sm ${
                            t.side === "LONG"
                              ? "bg-signal-buy/10 text-signal-buy"
                              : "bg-signal-sell/10 text-signal-sell"
                          }`}
                        >
                          {t.side}
                        </span>
                      </td>
                      <td className="text-right px-4 py-3" data-testid={`hist-conf-${t.id}`}>
                        {(t.ai_mode === "neurobro" || t.ai_mode === "claude") && t.score != null ? (
                          <span className={t.score >= 70 ? "text-signal-buy" : t.score >= 50 ? "text-signal-warn" : "text-signal-sell"}>
                            {Math.round(t.score)}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="text-right px-4 py-3">{fmt(t.entry_price)}</td>
                      <td className="text-right px-4 py-3">{fmt(t.exit_price)}</td>
                      <td className="text-right px-4 py-3">{t.leverage}x</td>
                      <td className={`text-right px-4 py-3 ${pnlPos ? "text-signal-buy" : "text-signal-sell"}`}>
                        {pnlPos ? "+" : ""}${fmt(t.pnl, 2)}
                        <div className="text-[10px] opacity-70">
                          {pnlPos ? "+" : ""}{fmt(t.pnl_pct, 2)}%
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] font-mono text-muted-foreground uppercase">
                          {t.reason}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

const Stat = ({ label, value, color = "text-white" }) => (
  <div className="border border-border rounded-sm bg-[#0d0d0d] p-4">
    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`font-mono font-bold text-2xl mt-1 ${color}`}>{value}</div>
  </div>
);
